/**
 * Zustand store for tracking ongoing activities/processes
 *
 * Centralized state for long-running operations like:
 * - Metadata sync (full/single type)
 * - Security scanning
 * - Deployments
 * - Any other async operations
 *
 * This allows us to:
 * - Disable certain UI actions during critical processes
 * - Show global activity indicators
 * - Prevent conflicting operations
 *
 * ORGANIZATION SCOPING
 * --------------------
 * Every activity here describes work happening inside ONE organization — a scan
 * of Org A's metadata, a run against Org A's records. This store used to hold a
 * single flat Map, so the header indicator showed Org A's scan to Org B and kept
 * showing it until a page refresh.
 *
 * Activities are now tagged with the organization that started them
 * ({@link Activity.orgId}, captured from `orgScope` at `startActivity` time — no
 * caller passes it, so no call site can forget it). The store publishes two
 * things:
 *
 *   allActivities — every organization's activities. Producers address these by
 *                   id, so a late SSE frame still updates the run it belongs to.
 *   activities    — the ACTIVE organization's activities. Everything that
 *                   renders, and every `is…Running()` selector, reads this.
 *
 * Activities are not discarded on a switch: the work is still running on the
 * server, and switching back to Org A should show it, still ticking. They are
 * simply invisible from anywhere else.
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  getActiveOrgId,
  registerOrgScopedState,
  type OrgId,
} from '@/lib/org-scope';

// ============================================================================
// Types
// ============================================================================

export type ActivityType =
  | 'metadata_sync_full'
  | 'metadata_sync_type'
  | 'metadata_initial_sync'
  | 'security_scan'
  | 'rag_ingest'
  | 'deployment'
  | 'file_sync'
  | 'release_ingest'
  | 'release_scan'
  | 'release_pipeline'
  | 'agent_run'
  | 'playbook_run'
  | 'salesforce_push';

export type ActivityStatus = 'pending' | 'in_progress' | 'completed' | 'failed';

export interface Activity {
  id: string;
  type: ActivityType;
  status: ActivityStatus;
  progress: number;  // 0-100
  /**
   * The producer never declared how much work there is, so `progress` is not a
   * meaningful percentage. Renderers must show an indeterminate bar instead of a
   * number — printing "0%" for a run that has already completed steps is just as
   * dishonest as inventing a denominator to make it climb.
   */
  indeterminate?: boolean;
  /** Units of work finished, when the total is unknown. Shown instead of a percent. */
  completedSteps?: number;
  message: string;
  startedAt: Date;
  completedAt?: Date;
  metadata?: Record<string, unknown>;  // Additional context (e.g., metadataType for single type sync)
  /**
   * The organization this work belongs to, captured when the activity started.
   * `null` only for activity begun before an organization was selected.
   */
  orgId: OrgId | null;
}

interface ActivityState {
  /**
   * Every activity, across every organization. Producers update by id; nothing
   * that renders reads this.
   */
  allActivities: Map<string, Activity>;

  /**
   * The active organization's activities — the only view the UI and the
   * `is…Running()` selectors read. Derived from {@link allActivities}; treat as
   * read-only.
   */
  activities: Map<string, Activity>;

  // Actions
  startActivity: (id: string, type: ActivityType, message?: string, metadata?: Record<string, unknown>) => void;
  updateActivity: (id: string, updates: Partial<Pick<Activity, 'status' | 'progress' | 'indeterminate' | 'completedSteps' | 'message' | 'metadata'>>) => void;
  completeActivity: (id: string, message?: string) => void;
  failActivity: (id: string, message?: string) => void;
  removeActivity: (id: string) => void;
  clearCompletedActivities: () => void;
  /** Forget one organization's activities. Sign-out and tests only — see the implementation. */
  clearActivitiesForOrg: (orgId: OrgId | null) => void;

  // Selectors — all scoped to the ACTIVE organization
  getActivity: (id: string) => Activity | undefined;
  getActivitiesByType: (type: ActivityType) => Activity[];
  hasActiveActivity: (type?: ActivityType) => boolean;
  isAnyActivityRunning: () => boolean;
  
  // Specific checks for common use cases
  isMetadataSyncing: () => boolean;
  isSecurityScanning: () => boolean;
  isIngesting: () => boolean;
  isDeploying: () => boolean;
  isAgentRunning: () => boolean;
  isPlaybookRunning: () => boolean;
  canSwitchOrg: () => boolean;
}

// ============================================================================
// Org-scoped view
// ============================================================================

/**
 * Rebuild the published view from the full set.
 *
 * O(n) on every mutation, with n bounded by the number of concurrent operations
 * a single user can start — a handful. An index keyed by org would be faster and
 * would also be a second structure to keep consistent with the first, which is
 * how the original bug was written.
 */
function publish(allActivities: Map<string, Activity>): Pick<ActivityState, 'allActivities' | 'activities'> {
  const activeOrgId = getActiveOrgId();
  const activities = new Map<string, Activity>();
  for (const [id, activity] of allActivities) {
    if (activity.orgId === activeOrgId) activities.set(id, activity);
  }
  return { allActivities, activities };
}

/** Apply `mutate` to a copy of the full set, then republish. */
function mutateAll(
  state: ActivityState,
  mutate: (draft: Map<string, Activity>) => boolean,
): Partial<ActivityState> | ActivityState {
  const draft = new Map(state.allActivities);
  if (!mutate(draft)) return state;
  return publish(draft);
}

// ============================================================================
// Store
// ============================================================================

export const useActivityStore = create<ActivityState>()(
  devtools(
    (set, get) => ({
      allActivities: new Map(),
      activities: new Map(),

      startActivity: (id, type, message = 'Starting...', metadata) => {
        set((state) =>
          mutateAll(state, (draft) => {
            const existing = draft.get(id);
            draft.set(id, {
              id,
              type,
              status: 'in_progress',
              // Preserve progress if reconnecting to an already-in-progress activity so the
              // progress bar doesn't jump back to 0% on every SSE reconnect.
              progress: existing?.status === 'in_progress' ? existing.progress : 0,
              message,
              startedAt: existing?.startedAt ?? new Date(),
              metadata,
              // Captured here rather than passed in: an activity always belongs
              // to the org that was on screen when the user started it, and a
              // parameter is something ~15 call sites can forget.
              orgId: existing?.orgId ?? getActiveOrgId(),
            });
            return true;
          }),
        );
      },

      updateActivity: (id, updates) => {
        set((state) =>
          mutateAll(state, (draft) => {
            const activity = draft.get(id);
            if (!activity) return false;
            draft.set(id, { ...activity, ...updates });
            return true;
          }),
        );
      },

      completeActivity: (id, message = 'Completed') => {
        set((state) =>
          mutateAll(state, (draft) => {
            const activity = draft.get(id);
            if (!activity) return false;
            draft.set(id, {
              ...activity,
              status: 'completed',
              progress: 100,
              // The run finished, so the total is finally known — drop back to a
              // determinate 100% rather than leaving the bar pulsing forever.
              indeterminate: false,
              message,
              completedAt: new Date(),
            });
            return true;
          }),
        );
      },

      failActivity: (id, message = 'Failed') => {
        set((state) =>
          mutateAll(state, (draft) => {
            const activity = draft.get(id);
            if (!activity) return false;
            draft.set(id, {
              ...activity,
              status: 'failed',
              message,
              completedAt: new Date(),
            });
            return true;
          }),
        );
      },

      removeActivity: (id) => {
        set((state) =>
          mutateAll(state, (draft) => draft.delete(id)),
        );
      },

      clearCompletedActivities: () => {
        set((state) =>
          mutateAll(state, (draft) => {
            let changed = false;
            for (const [id, activity] of draft) {
              if (activity.status === 'completed' || activity.status === 'failed') {
                draft.delete(id);
                changed = true;
              }
            }
            return changed;
          }),
        );
      },

      /**
       * Forget an organization's activities entirely.
       *
       * Not called on an org switch — a running scan should still be there when
       * the user comes back. Reserved for sign-out, where the state has no owner
       * left, and for tests.
       */
      clearActivitiesForOrg: (orgId) => {
        set((state) =>
          mutateAll(state, (draft) => {
            let changed = false;
            for (const [id, activity] of draft) {
              if (activity.orgId === orgId) {
                draft.delete(id);
                changed = true;
              }
            }
            return changed;
          }),
        );
      },

      // Selectors
      //
      // Everything that answers "what is running?" reads `activities` — the
      // active organization's view. `getActivity` is the exception: it answers
      // "what is THIS activity?" for a producer that already holds the id
      // (e.g. a stream handler reading back its own metadata), and that producer
      // may legitimately be running under an organization the user has left.
      getActivity: (id) => {
        return get().allActivities.get(id);
      },

      getActivitiesByType: (type) => {
        const activities: Activity[] = [];
        for (const activity of get().activities.values()) {
          if (activity.type === type) {
            activities.push(activity);
          }
        }
        return activities;
      },

      hasActiveActivity: (type) => {
        for (const activity of get().activities.values()) {
          const isActive = activity.status === 'pending' || activity.status === 'in_progress';
          if (type) {
            if (activity.type === type && isActive) return true;
          } else {
            if (isActive) return true;
          }
        }
        return false;
      },

      isAnyActivityRunning: () => {
        for (const activity of get().activities.values()) {
          if (activity.status === 'pending' || activity.status === 'in_progress') {
            return true;
          }
        }
        return false;
      },

      // Specific checks
      isMetadataSyncing: () => {
        for (const activity of get().activities.values()) {
          if (
            (activity.type === 'metadata_sync_full' || activity.type === 'metadata_sync_type') &&
            (activity.status === 'pending' || activity.status === 'in_progress')
          ) {
            return true;
          }
        }
        return false;
      },

      isSecurityScanning: () => {
        return get().hasActiveActivity('security_scan');
      },

      isIngesting: () => {
        return get().hasActiveActivity('rag_ingest');
      },

      isDeploying: () => {
        return get().hasActiveActivity('deployment');
      },

      isAgentRunning: () => {
        return get().hasActiveActivity('agent_run');
      },

      isPlaybookRunning: () => {
        return get().hasActiveActivity('playbook_run');
      },

      canSwitchOrg: () => {
        // Can't switch org if any critical process is running
        const state = get();
        return !state.isMetadataSyncing() && !state.isDeploying() && !state.isSecurityScanning();
      },
    }),
    { name: 'activity-store' }
  )
);

// ============================================================================
// Organization scoping
// ============================================================================

/**
 * Re-derive the published view whenever the active organization changes.
 *
 * Nothing is deleted: the underlying jobs keep running on the server, and
 * returning to Org A should show its scan exactly where it got to. They simply
 * stop being visible from Org B.
 */
registerOrgScopedState({
  name: 'activityStore',
  onOrgChange: () => {
    useActivityStore.setState((state) => publish(new Map(state.allActivities)));
  },
  onSignOut: () => {
    useActivityStore.setState({ allActivities: new Map(), activities: new Map() });
  },
});

// ============================================================================
// Selectors for better performance (use outside of components)
// ============================================================================

export const selectActivities = (state: ActivityState) => state.activities;
export const selectIsAnyActivityRunning = (state: ActivityState) => state.isAnyActivityRunning();
export const selectCanSwitchOrg = (state: ActivityState) => state.canSwitchOrg();
export const selectIsMetadataSyncing = (state: ActivityState) => state.isMetadataSyncing();
export const selectIsDeploying = (state: ActivityState) => state.isDeploying();
export const selectIsSecurityScanning = (state: ActivityState) => state.isSecurityScanning();
export const selectIsIngesting = (state: ActivityState) => state.isIngesting();
